import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Eye, EyeOff, Lock, Mail, Shield } from 'lucide-react';
import { ShimmerButton, useToast } from '@raffle/ui';
import { useAuth } from '../../providers/AuthProvider';

export const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const { login, isLoading } = useAuth();
  const toast = useToast();
  
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email || !password) {
      toast.error('Missing Credentials', 'Please enter your email and password');
      return;
    }

    try {
      await login(email, password);
      navigate('/');
    } catch {
      toast.error('Login Failed', 'Invalid email or password');
    }
  };

  return (
    <div className="min-h-screen bg-surface-deepest flex">
      {/* Left - Branding */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
        {/* Nigerian Flag Stripe */}
        <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-[#008751] via-white to-[#008751] z-20" />
        
        {/* Background Pattern */}
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_30%,rgba(0,135,81,0.15),transparent_50%)]" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_70%,rgba(212,175,55,0.1),transparent_50%)]" />
        
        {/* Adire Pattern */}
        <div
          className="absolute inset-0 opacity-[0.03]"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23D4AF37' fill-opacity='1'%3E%3Cpath d='M30 30c0-5.5 4.5-10 10-10s10 4.5 10 10-4.5 10-10 10-10-4.5-10-10zm-20 0c0-5.5 4.5-10 10-10s10 4.5 10 10-4.5 10-10 10-10-4.5-10-10z'/%3E%3Ccircle cx='30' cy='30' r='3'/%3E%3Ccircle cx='10' cy='10' r='3'/%3E%3Ccircle cx='50' cy='10' r='3'/%3E%3Ccircle cx='10' cy='50' r='3'/%3E%3Ccircle cx='50' cy='50' r='3'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }}
        />

        {/* Content */}
        <div className="relative z-10 flex flex-col justify-center px-16">
          {/* Logo */}
          <div className="flex items-center gap-3 mb-12">
            <div className="w-12 h-12 rounded-vault-lg bg-gradient-to-br from-[#008751] to-gold-500 flex items-center justify-center relative overflow-hidden">
              <div className="absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r from-[#008751] via-white to-[#008751]" />
              <span className="text-xl font-bold text-white">NV</span>
            </div>
            <span className="text-2xl font-display font-bold text-white">
              Naija<span className="text-gold-500">Vault</span>
            </span>
          </div>

          <h1 className="font-display text-4xl font-bold text-white mb-4 leading-tight">
            Admin Control
            <br />
            <span className="text-[#00B36B]">Center</span> 🇳🇬
          </h1>
          
          <p className="text-text-secondary text-lg max-w-md mb-8">
            Manage properties, monitor sales, and oversee all platform operations for Nigeria's premier property raffle.
          </p>

          {/* Features */}
          <div className="space-y-4">
            {[
              'Real-time analytics and reporting',
              'Property and draw management',
              'Customer support tools',
              'Secure payment oversight',
            ].map((feature, index) => (
              <motion.div
                key={feature}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.2 + index * 0.1 }}
                className="flex items-center gap-3"
              >
                <div className="w-6 h-6 rounded-full bg-[#008751]/20 flex items-center justify-center">
                  <div className="w-2 h-2 rounded-full bg-[#00B36B]" />
                </div>
                <span className="text-text-secondary">{feature}</span>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Decorative Elements */}
        <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#008751]/30 to-transparent" />
      </div>

      {/* Right - Login Form */}
      <div className="flex-1 flex items-center justify-center p-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-md"
        >
          {/* Mobile Logo */}
          <div className="flex items-center justify-center gap-3 mb-8 lg:hidden">
            <div className="w-10 h-10 rounded-vault bg-gradient-to-br from-[#008751] to-gold-500 flex items-center justify-center relative overflow-hidden">
              <div className="absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r from-[#008751] via-white to-[#008751]" />
              <span className="text-white font-bold">NV</span>
            </div>
            <span className="text-xl font-display font-bold text-white">
              Naija<span className="text-gold-500">Vault</span>
            </span>
          </div>

          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[#008751]/10 border border-[#008751]/20 mb-4">
              <Shield className="w-8 h-8 text-[#00B36B]" />
            </div>
            <h2 className="font-display text-2xl font-bold text-white mb-2">
              Admin Login
            </h2>
            <p className="text-text-secondary">
              Enter your credentials to access the dashboard
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-text-secondary mb-2">
                Email Address
              </label>
              <div className="relative">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-text-muted" />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="admin@naijavault.com"
                  className="w-full h-12 pl-12 pr-4 bg-surface-2 border border-white/10 rounded-vault text-text-primary placeholder:text-text-muted focus:outline-none focus:border-[#008751]/50 transition-colors"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-text-secondary mb-2">
                Password
              </label>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-text-muted" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="w-full h-12 pl-12 pr-12 bg-surface-2 border border-white/10 rounded-vault text-text-primary placeholder:text-text-muted focus:outline-none focus:border-[#008751]/50 transition-colors"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-text-muted hover:text-text-primary transition-colors"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <div className="flex items-center justify-between">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  className="w-4 h-4 rounded border-white/20 bg-surface-2 text-[#008751] focus:ring-[#008751]/50"
                />
                <span className="text-sm text-text-secondary">Remember me</span>
              </label>
              <button
                type="button"
                className="text-sm text-[#00B36B] hover:text-[#008751] transition-colors"
              >
                Forgot password?
              </button>
            </div>

            <ShimmerButton type="submit" fullWidth loading={isLoading}>
              Sign In
            </ShimmerButton>
          </form>

          <div className="mt-8 pt-6 border-t border-white/10 text-center">
            <p className="text-sm text-text-muted">
              Authorized personnel only. All access is logged and monitored.
            </p>
          </div>
        </motion.div>
      </div>
    </div>
  );
};
